<script setup>
import { computed } from 'vue';
import { useAccount } from 'dashboard/composables/useAccount';

const props = defineProps({
  isOpen: {
    type: Boolean,
    default: false,
  },
  reloadKey: {
    type: Number,
    default: 0,
  },
});

const emit = defineEmits(['toggle']);
const { currentAccount } = useAccount();

const normalizeUrl = url => {
  if (!url) return '';
  if (url.startsWith('http://') || url.startsWith('https://')) return url;
  if (url.startsWith('//')) return `https:${url}`;

  return `https://${url}`;
};

const baseUrl = computed(
  () => window.chatwootConfig?.ticketDetailSidebarEmbedUrl || ''
);
const isConfigured = computed(() => !!baseUrl.value);
const cmsProjectId = computed(() => currentAccount.value?.site_id || '');
const hasProjectId = computed(() => !!cmsProjectId.value);

const iframeSrc = computed(() => {
  if (!props.isOpen) return '';
  if (!isConfigured.value) return '';
  if (!hasProjectId.value) return '';

  const url = new URL(normalizeUrl(baseUrl.value));
  url.searchParams.set('cms_project_id', cmsProjectId.value);
  return url.toString();
});
</script>

<template>
  <div>
    <button
      type="button"
      class="flex items-center justify-between w-full rounded-lg bg-n-slate-2 outline outline-1 outline-n-weak m-0 cursor-pointer py-2 px-4"
      @click.stop="emit('toggle')"
    >
      <div class="flex items-center gap-2 min-w-0">
        <span class="i-lucide-file-text text-n-slate-11 text-base" />
        <h5 class="text-n-slate-12 text-sm mb-0 py-0 pr-2 pl-0 truncate">
          {{ $t('CONVERSATION.SIDEBAR.TICKET_DETAIL_PANEL.TITLE') }}
        </h5>
      </div>
      <div class="flex justify-end w-3 text-n-blue-text">
        <fluent-icon v-if="isOpen" size="24" icon="subtract" type="solid" />
        <fluent-icon v-else size="24" icon="add" type="solid" />
      </div>
    </button>

    <div
      v-if="isOpen"
      class="bg-n-background outline outline-1 outline-n-weak -mt-[-1px] border-t-0 rounded-br-lg rounded-bl-lg p-2"
    >
      <p v-if="!isConfigured" class="text-sm text-n-slate-11 mb-0 px-2 py-1">
        {{ $t('CONVERSATION.SIDEBAR.TICKET_DETAIL_PANEL.NOT_CONFIGURED') }}
      </p>
      <p
        v-else-if="!hasProjectId"
        class="text-sm text-n-slate-11 mb-0 px-2 py-1"
      >
        {{ $t('CONVERSATION.SIDEBAR.TICKET_DETAIL_PANEL.NO_PROJECT') }}
      </p>
      <div
        v-else
        class="w-full h-[560px] max-h-[65vh] rounded-md overflow-hidden"
      >
        <iframe
          :key="reloadKey"
          class="w-full h-full bg-n-background"
          :src="iframeSrc"
          :title="$t('CONVERSATION.SIDEBAR.TICKET_DETAIL_PANEL.TITLE')"
          allow="clipboard-write"
        />
      </div>
    </div>
  </div>
</template>
